import { motion } from "motion/react";
import { downloadSlumpTestPDF } from "../utils/slumpPdfGenerator";

interface SlumpTestReportPreviewProps {
  batchId: string;
  reportId: string;
  testDate: string;
  testTime: string;
  slumpValue: number;
  temperature: number;
  density: number;
  grade: string;
  location: string;
  weather: string;
  customerName?: string;
  projectName?: string;
  onClose: () => void;
}

export default function SlumpTestReportPreview({
  batchId,
  reportId,
  testDate,
  testTime,
  slumpValue,
  temperature,
  density,
  grade,
  location,
  weather,
  customerName = "ABC Construction Ltd.",
  projectName = "Metro Tower Phase 2",
  onClose,
}: SlumpTestReportPreviewProps) {
  // Calculate compliance
  const slumpCompliance = slumpValue >= 75 && slumpValue <= 100 ? "PASS" : "FAIL";
  const temperatureCompliance = temperature <= 32 ? "PASS" : "FAIL";
  const densityCompliance = density >= 2400 && density <= 2500 ? "PASS" : "FAIL";
  const overallCompliance = slumpCompliance === "PASS" && temperatureCompliance === "PASS" && densityCompliance === "PASS";

  const handleDownloadPDF = () => {
    try {
      downloadSlumpTestPDF({
        reportId,
        batchId,
        testDate,
        testTime,
        slumpValue,
        temperature,
        density,
        grade,
        location,
        weather,
        customerName,
        projectName,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: `Slump Test Report - ${reportId}`,
      text: `Test Results:\nSlump: ${slumpValue}mm (${slumpCompliance})\nTemperature: ${temperature}°C (${temperatureCompliance})\nDensity: ${density}kg/m³ (${densityCompliance})\n\nOverall Status: ${overallCompliance ? "COMPLIANT" : "NON-COMPLIANT"}`,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Try clipboard as fallback, but handle if it's blocked
        try {
          await navigator.clipboard.writeText(`${shareData.title}\n\n${shareData.text}`);
          alert("Report details copied to clipboard!");
        } catch (clipboardError) {
          alert("Share feature not available in this environment");
        }
      }
    } catch (error) {
      console.log("Share cancelled or failed");
    }
  };

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-[#0A0F1C]/60 z-[80]"
        style={{ backdropFilter: "blur(8px)" }}
      />

      {/* Report Preview Modal */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed inset-4 z-[90] flex items-center justify-center"
      >
        <div
          className="w-full max-w-2xl h-full max-h-[90vh] bg-white rounded-3xl overflow-hidden flex flex-col"
          style={{
            boxShadow: "0 24px 64px rgba(0, 0, 0, 0.24), 0 0 0 1px rgba(0, 0, 0, 0.08)",
          }}
        >
          {/* Header Actions */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
            <div>
              <div
                className="text-[#0A0F1C]"
                style={{ fontSize: "14px", fontWeight: "700", letterSpacing: "-0.01em" }}
              >
                Slump Test Report Preview
              </div>
              <div
                className="text-[#717182] mt-0.5"
                style={{ fontSize: "11px", fontWeight: "500" }}
              >
                {reportId}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleShare}
                className="px-4 py-2 rounded-xl bg-[#F9FAFB] text-[#1B2A5B] border border-[#1B2A5B]/20"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Share
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={handleDownloadPDF}
                className="px-4 py-2 rounded-xl bg-[#3B82F6] text-white"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                Download PDF
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="w-9 h-9 rounded-xl bg-[#F9FAFB] flex items-center justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5 5l10 10M15 5l-10 10"
                    stroke="#0A0F1C"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.button>
            </div>
          </div>

          {/* Report Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-8 bg-[#F9FAFB]">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 max-w-[700px] mx-auto"
              style={{
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04)",
              }}
            >
              {/* Report Header */}
              <div className="text-center mb-8 pb-6 border-b-2 border-[#1B2A5B]">
                <div
                  className="text-[#1B2A5B] mb-2"
                  style={{ fontSize: "10px", letterSpacing: "0.12em", fontWeight: "800" }}
                >
                  TRACK CONCRETE QUALITY CONTROL LABORATORY
                </div>
                <div
                  className="text-[#717182] mb-3"
                  style={{ fontSize: "8px", letterSpacing: "0.05em" }}
                >
                  NABL Accredited Lab | Certificate No: TC-5678 | ISO 9001:2015 Certified
                </div>
                <div
                  className="text-[#0A0F1C] mb-1"
                  style={{ fontSize: "20px", fontWeight: "700", letterSpacing: "-0.02em" }}
                >
                  SLUMP TEST REPORT
                </div>
                <div
                  className="text-[#717182]"
                  style={{ fontSize: "10px", letterSpacing: "0.02em" }}
                >
                  As per IS 1199-1959 (Reaffirmed 2004)
                </div>
              </div>

              {/* Report ID and Status Badge */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "9px", letterSpacing: "0.05em", fontWeight: "600" }}
                  >
                    REPORT NO.
                  </div>
                  <div
                    className="text-[#0A0F1C]"
                    style={{ fontSize: "14px", fontWeight: "700" }}
                  >
                    {reportId}
                  </div>
                </div>
                <div
                  className="px-4 py-2 rounded-lg"
                  style={{
                    backgroundColor: overallCompliance ? "#0F3D2E" : "#5C1111",
                    color: "white",
                  }}
                >
                  <div
                    style={{ fontSize: "11px", letterSpacing: "0.08em", fontWeight: "800" }}
                  >
                    {overallCompliance ? "✓ COMPLIANT" : "✕ NON-COMPLIANT"}
                  </div>
                </div>
              </div>

              {/* Project & Customer Details */}
              <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: "#F9FAFB" }}>
                <div
                  className="text-[#717182] mb-3"
                  style={{ fontSize: "9px", letterSpacing: "0.08em", fontWeight: "700" }}
                >
                  PROJECT DETAILS
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Customer Name
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "600" }}
                    >
                      {customerName}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Project Name
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "600" }}
                    >
                      {projectName}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Mix Design ID
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "600" }}
                    >
                      {batchId}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Sample ID
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "700" }}
                    >
                      SMP-{batchId.split('-').pop()}-F1
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Concrete Grade
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "700" }}
                    >
                      {grade}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      W/C Ratio
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "700" }}
                    >
                      0.45
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Information */}
              <div className="mb-6 p-4 rounded-xl border border-[#E5E7EB]">
                <div
                  className="text-[#717182] mb-3"
                  style={{ fontSize: "9px", letterSpacing: "0.08em", fontWeight: "700" }}
                >
                  TEST INFORMATION
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Date of Test
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "600" }}
                    >
                      {testDate}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Time of Test
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "600" }}
                    >
                      {testTime}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Test Location
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "600" }}
                    >
                      {location}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "9px", fontWeight: "600" }}
                    >
                      Weather Conditions
                    </div>
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "11px", fontWeight: "600" }}
                    >
                      {weather}
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Results - Main Focus */}
              <div className="mb-6">
                <div
                  className="text-[#0A0F1C] mb-4 pb-2 border-b-2 border-[#1B2A5B]"
                  style={{ fontSize: "13px", letterSpacing: "0.05em", fontWeight: "700" }}
                >
                  TEST RESULTS
                </div>

                {/* Slump Test */}
                <div className="mb-4 p-5 rounded-xl" style={{ backgroundColor: "#F9FAFB" }}>
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "10px", letterSpacing: "0.08em", fontWeight: "700" }}
                    >
                      SLUMP VALUE
                    </div>
                    <div
                      className="px-3 py-1 rounded-md"
                      style={{
                        backgroundColor: slumpCompliance === "PASS" ? "#0F3D2E" : "#5C1111",
                        color: "white",
                        fontSize: "9px",
                        fontWeight: "800",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {slumpCompliance}
                    </div>
                  </div>
                  <div className="flex items-end gap-3 mb-4">
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "48px", fontWeight: "700", lineHeight: "1", letterSpacing: "-0.02em" }}
                    >
                      {slumpValue}
                    </div>
                    <div
                      className="text-[#717182] pb-2"
                      style={{ fontSize: "18px", fontWeight: "600" }}
                    >
                      mm
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-white">
                      <span className="text-[#717182]" style={{ fontSize: "10px", fontWeight: "600" }}>
                        Specified Range
                      </span>
                      <span className="text-[#0A0F1C]" style={{ fontSize: "10px", fontWeight: "700" }}>
                        75 - 100 mm
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-white">
                      <span className="text-[#717182]" style={{ fontSize: "10px", fontWeight: "600" }}>
                        Deviation
                      </span>
                      <span className="text-[#0A0F1C]" style={{ fontSize: "10px", fontWeight: "700" }}>
                        {slumpValue >= 75 && slumpValue <= 100 ? "Within Limits" : "Out of Range"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Temperature Test */}
                <div className="mb-4 p-5 rounded-xl border border-[#E5E7EB]">
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "10px", letterSpacing: "0.08em", fontWeight: "700" }}
                    >
                      CONCRETE TEMPERATURE
                    </div>
                    <div
                      className="px-3 py-1 rounded-md"
                      style={{
                        backgroundColor: temperatureCompliance === "PASS" ? "#0F3D2E" : "#5C1111",
                        color: "white",
                        fontSize: "9px",
                        fontWeight: "800",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {temperatureCompliance}
                    </div>
                  </div>
                  <div className="flex items-end gap-3 mb-4">
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "42px", fontWeight: "700", lineHeight: "1", letterSpacing: "-0.02em" }}
                    >
                      {temperature}
                    </div>
                    <div
                      className="text-[#717182] pb-2"
                      style={{ fontSize: "18px", fontWeight: "600" }}
                    >
                      °C
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-[#F9FAFB]">
                      <span className="text-[#717182]" style={{ fontSize: "10px", fontWeight: "600" }}>
                        Maximum Limit
                      </span>
                      <span className="text-[#0A0F1C]" style={{ fontSize: "10px", fontWeight: "700" }}>
                        ≤ 32°C
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#F9FAFB]">
                      <span className="text-[#717182]" style={{ fontSize: "10px", fontWeight: "600" }}>
                        Compliance Status
                      </span>
                      <span className="text-[#0A0F1C]" style={{ fontSize: "10px", fontWeight: "700" }}>
                        {temperature <= 32 ? "Acceptable" : "Exceeds Limit"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Density Test */}
                <div className="mb-4 p-5 rounded-xl border border-[#E5E7EB]">
                  <div className="flex items-center justify-between mb-4">
                    <div
                      className="text-[#717182]"
                      style={{ fontSize: "10px", letterSpacing: "0.08em", fontWeight: "700" }}
                    >
                      FRESH CONCRETE DENSITY
                    </div>
                    <div
                      className="px-3 py-1 rounded-md"
                      style={{
                        backgroundColor: densityCompliance === "PASS" ? "#0F3D2E" : "#5C1111",
                        color: "white",
                        fontSize: "9px",
                        fontWeight: "800",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {densityCompliance}
                    </div>
                  </div>
                  <div className="flex items-end gap-3 mb-4">
                    <div
                      className="text-[#0A0F1C]"
                      style={{ fontSize: "38px", fontWeight: "700", lineHeight: "1", letterSpacing: "-0.02em" }}
                    >
                      {density}
                    </div>
                    <div
                      className="text-[#717182] pb-2"
                      style={{ fontSize: "16px", fontWeight: "600" }}
                    >
                      kg/m³
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2 border-b border-[#F9FAFB]">
                      <span className="text-[#717182]" style={{ fontSize: "10px", fontWeight: "600" }}>
                        Specified Range
                      </span>
                      <span className="text-[#0A0F1C]" style={{ fontSize: "10px", fontWeight: "700" }}>
                        2400 - 2500 kg/m³
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-[#F9FAFB]">
                      <span className="text-[#717182]" style={{ fontSize: "10px", fontWeight: "600" }}>
                        Compliance Status
                      </span>
                      <span className="text-[#0A0F1C]" style={{ fontSize: "10px", fontWeight: "700" }}>
                        {density >= 2400 && density <= 2500 ? "Within Limits" : "Out of Range"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Remarks & Conclusion */}
              <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: "#FEF3C7" }}>
                <div
                  className="text-[#78350F] mb-2"
                  style={{ fontSize: "10px", letterSpacing: "0.08em", fontWeight: "700" }}
                >
                  REMARKS
                </div>
                <div
                  className="text-[#78350F]"
                  style={{ fontSize: "11px", lineHeight: "1.5" }}
                >
                  {overallCompliance
                    ? "The fresh concrete sample has passed all slump test requirements. The concrete is suitable for construction use as per specifications."
                    : "The fresh concrete sample has failed one or more test requirements. Please review the deviations and take corrective action before use."}
                </div>
              </div>

              {/* Signatures */}
              <div className="grid grid-cols-2 gap-8 pt-6 mt-6 border-t border-[#E5E7EB]">
                <div>
                  <div className="h-12 mb-2 border-b border-[#0A0F1C]"></div>
                  <div
                    className="text-[#0A0F1C]"
                    style={{ fontSize: "10px", fontWeight: "600" }}
                  >
                    Rajesh Kumar M.
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "9px" }}
                  >
                    Quality Control Engineer
                  </div>
                  <div
                    className="text-[#717182] mt-0.5"
                    style={{ fontSize: "8px" }}
                  >
                    Reg. No: QCE/2547
                  </div>
                </div>
                <div>
                  <div className="h-12 mb-2 border-b border-[#0A0F1C]"></div>
                  <div
                    className="text-[#0A0F1C]"
                    style={{ fontSize: "10px", fontWeight: "600" }}
                  >
                    Dr. Suresh Patel
                  </div>
                  <div
                    className="text-[#717182]"
                    style={{ fontSize: "9px" }}
                  >
                    Lab Manager (NABL Approved)
                  </div>
                  <div
                    className="text-[#717182] mt-0.5"
                    style={{ fontSize: "8px" }}
                  >
                    NABL ID: LM-8934
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-[#E5E7EB] text-center">
                <div
                  className="text-[#717182]"
                  style={{ fontSize: "8px", letterSpacing: "0.02em" }}
                >
                  This is a computer-generated report. Authorized signatures are maintained digitally.
                </div>
                <div
                  className="text-[#717182] mt-1"
                  style={{ fontSize: "8px" }}
                >
                  Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </>
  );
}